import { useState, useMemo, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import useApiFetch from '@/hooks/useApiFetch';
import useContractorManagement from '@/hooks/useContractorManagement';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import { getActiveOrgId } from '@/lib/activeOrg';
import financeContractorsService, { ContractorReconciliation } from '@/services/financeContractors';
import financeVendorsService from '@/services/financeVendors';
import financeWarpService, { WarpConnectionStatus, WarpWorker } from '@/services/financeWarp';
import { FetchApiIds } from '@/types';
import FinanceXContractorsComponent from './FinanceXContractors.component';

export type DatePreset =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'this_year'
  | 'last_year'
  | 'custom';

export type DateFilter = {
  preset: DatePreset;
  startDate: string;
  endDate: string;
};

function lastDayOfMonth(year: number, month: number): string {
  return new Date(year, month + 1, 0).toISOString().split('T')[0];
}

/** Cap end date at today so cost calculations don't exceed the period with actual data */
function capAtToday(endDate: string): string {
  const today = new Date().toISOString().split('T')[0];
  return endDate > today ? today : endDate;
}

function getYearRange(year: number, cap = false): { startDate: string; endDate: string } {
  const endDate = `${year}-12-31`;
  return { startDate: `${year}-01-01`, endDate: cap ? capAtToday(endDate) : endDate };
}

function getMonthRange(offset: number): { startDate: string; endDate: string } {
  const now = new Date();
  const target = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const endDate = lastDayOfMonth(target.getFullYear(), target.getMonth());
  return { startDate: target.toISOString().split('T')[0], endDate: capAtToday(endDate) };
}

function getQuarterRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), q * 3, 1);
  const endDate = lastDayOfMonth(now.getFullYear(), q * 3 + 2);
  return { startDate: start.toISOString().split('T')[0], endDate: capAtToday(endDate) };
}

const DEFAULT: ContractorReconciliation = { contractors: [], totalBudgeted: 0, totalActualPaid: 0, totalVariance: 0, unlinkedPayments: [] };

const FinanceXContractorsContainer = function (): JSX.Element {
  const currentYear = new Date().getFullYear();
  const orgId = getActiveOrgId();
  const queryClient = useQueryClient();

  const [dateFilter, setDateFilter] = useState<DateFilter>({ preset: 'this_year', ...getYearRange(currentYear, true) });

  const handlePresetChange = useCallback((preset: DatePreset) => {
    switch (preset) {
      case 'this_month': setDateFilter({ preset, ...getMonthRange(0) }); break;
      case 'last_month': setDateFilter({ preset, ...getMonthRange(-1) }); break;
      case 'this_quarter': setDateFilter({ preset, ...getQuarterRange() }); break;
      case 'this_year': setDateFilter({ preset, ...getYearRange(currentYear, true) }); break;
      case 'last_year': setDateFilter({ preset, ...getYearRange(currentYear - 1) }); break;
      default: setDateFilter((prev) => ({ ...prev, preset }));
    }
  }, [currentYear]);

  const handleCustomDateChange = useCallback((startDate: string, endDate: string) => {
    setDateFilter({ preset: 'custom', startDate, endDate: capAtToday(endDate) });
  }, []);

  const { data, isLoading } = useApiFetch<ContractorReconciliation, void>(
    [FetchApiIds.FX_DASHBOARD_SUMMARY, 'contractor-recon', orgId, dateFilter.startDate, dateFilter.endDate],
    async () => {
      const result = await financeContractorsService.getReconciliation(dateFilter.startDate, dateFilter.endDate);
      return { data: result } as any;
    },
    { retry: false }
  );

  const recon = useMemo((): ContractorReconciliation => {
    const raw = data as any;
    const d = raw?.data ?? raw;
    return (d as ContractorReconciliation) ?? DEFAULT;
  }, [data]);

  const handleLinkVendor = useCallback(async (vendorId: string, profileId: string) => {
    await financeVendorsService.update(vendorId, { profileId });
    queryClient.invalidateQueries(FetchApiIds.FX_DASHBOARD_SUMMARY);
  }, [queryClient]);

  const management = useContractorManagement();

  // --- Warp roster ---
  const { data: warpStatus } = useSeoFetch<WarpConnectionStatus>(
    ['warp-connection'],
    () => financeWarpService.getConnectionStatus(),
    { retry: false }
  );

  const { data: warpContractors = [], refetch: refetchWarpContractors } = useSeoFetch<WarpWorker[]>(
    ['warp-contractors'],
    () => financeWarpService.getWorkers({ type: 'contractor' }),
    { retry: false }
  );

  const [isWarpSyncing, setIsWarpSyncing] = useState(false);
  const [isWarpLinking, setIsWarpLinking] = useState(false);
  const [warpMessage, setWarpMessage] = useState<string | null>(null);
  const [warpCreateProfiles, setWarpCreateProfiles] = useState(false);

  const refreshAll = useCallback(() => {
    refetchWarpContractors();
    queryClient.invalidateQueries(FetchApiIds.FX_DASHBOARD_SUMMARY);
  }, [refetchWarpContractors, queryClient]);

  const handleWarpSync = useCallback(async () => {
    setIsWarpSyncing(true);
    setWarpMessage(null);
    try {
      const result = await financeWarpService.syncWorkers({
        types: ['contractor'],
        createMissingProfiles: warpCreateProfiles,
      });
      const createdPart = result.profilesCreated ? ` · ${result.profilesCreated} profiles created` : '';
      const namePart = result.profilesLinkedByName ? ` · ${result.profilesLinkedByName} matched by name` : '';
      const ambigPart = result.ambiguousSkipped ? ` · ${result.ambiguousSkipped} ambiguous (skipped)` : '';
      setWarpMessage(
        `Synced ${result.contractors} contractors · ${result.linkedToProfiles} linked to profiles${createdPart}${namePart}${ambigPart}`
      );
      refreshAll();
    } catch (err: any) {
      setWarpMessage(`Error: ${err.message}`);
    } finally {
      setIsWarpSyncing(false);
    }
  }, [refreshAll, warpCreateProfiles]);

  const handleWarpLink = useCallback(async () => {
    setIsWarpLinking(true);
    setWarpMessage(null);
    try {
      const result = await financeWarpService.linkToVendors();
      setWarpMessage(`Linked ${result.linked} contractors to vendors (${result.created} new)`);
      refreshAll();
    } catch (err: any) {
      setWarpMessage(`Error: ${err.message}`);
    } finally {
      setIsWarpLinking(false);
    }
  }, [refreshAll]);

  return (
    <FinanceXContractorsComponent
      recon={recon}
      isLoading={isLoading}
      dateFilter={dateFilter}
      onPresetChange={handlePresetChange}
      onCustomDateChange={handleCustomDateChange}
      onLinkVendor={handleLinkVendor}
      management={management}
      warpConnected={!!warpStatus?.isConnected}
      warpContractors={warpContractors}
      isWarpSyncing={isWarpSyncing}
      isWarpLinking={isWarpLinking}
      warpMessage={warpMessage}
      warpCreateProfiles={warpCreateProfiles}
      onWarpCreateProfilesChange={setWarpCreateProfiles}
      onWarpSync={handleWarpSync}
      onWarpLink={handleWarpLink}
    />
  );
};

export default FinanceXContractorsContainer;
